import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "./store/authStore";
import ProtectedRoute from "./routes/ProtectedRoute";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import AdminLayout from "./layouts/AdminLayout";
import { PageLoader } from "./components/shared/Spinner";

// Login: eager (es la primera pantalla que ve el usuario)
import Login from "./pages/Login";

// Resto: lazy con React.lazy. Cada ruta es su propio chunk para que el
// bundle inicial sea sólo lo necesario para login. El editor (~250KB con
// hls.js detrás) ya no pesa cuando el usuario solo abre el dashboard.
const Dashboard      = lazy(() => import("./pages/superadmin/Dashboard"));
const Locales        = lazy(() => import("./pages/superadmin/Locales"));
const LocalDetalle   = lazy(() => import("./pages/superadmin/LocalDetalle"));
const WizardNuevoLocal = lazy(() => import("./pages/superadmin/WizardNuevoLocal"));
const Nodos          = lazy(() => import("./pages/superadmin/Nodos"));
const Usuarios       = lazy(() => import("./pages/superadmin/Usuarios"));
const Publicidad     = lazy(() => import("./pages/superadmin/Publicidad"));
const Auditoria      = lazy(() => import("./pages/superadmin/Auditoria"));

const LocalStream    = lazy(() => import("./pages/local/Stream"));
const LocalArchivos  = lazy(() => import("./pages/local/Archivos"));
const LocalHorarios  = lazy(() => import("./pages/local/Horarios"));
const LocalEditor    = lazy(() => import("./pages/local/Editor"));
const LocalPreview   = lazy(() => import("./pages/local/Preview"));

export default function App() {
  const rol = useAuthStore((s) => s.rol);

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route
            path="/superadmin/*"
            element={
              <ProtectedRoute rol="superadmin">
                <AdminLayout variant="superadmin">
                  <Routes>
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="locales" element={<Locales />} />
                    <Route path="locales/nuevo" element={<WizardNuevoLocal />} />
                    <Route path="locales/:id" element={<LocalDetalle />} />
                    <Route path="publicidad" element={<Publicidad />} />
                    <Route path="usuarios" element={<Usuarios />} />
                    <Route path="nodos" element={<Nodos />} />
                    <Route path="auditoria" element={<Auditoria />} />
                    <Route path="*" element={<Navigate to="dashboard" replace />} />
                  </Routes>
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/local/*"
            element={
              <ProtectedRoute rol="admin_local">
                <AdminLayout variant="local">
                  <Routes>
                    <Route path="stream" element={<LocalStream />} />
                    <Route path="editor" element={<LocalEditor />} />
                    <Route path="archivos" element={<LocalArchivos />} />
                    <Route path="horarios" element={<LocalHorarios />} />
                    <Route path="preview" element={<LocalPreview />} />
                    <Route path="*" element={<Navigate to="stream" replace />} />
                  </Routes>
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="*"
            element={
              <Navigate
                to={
                  rol === "superadmin"
                    ? "/superadmin/dashboard"
                    : rol === "admin_local"
                    ? "/local/stream"
                    : "/login"
                }
                replace
              />
            }
          />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
